import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * 방금 한 일과, 그것을 무르는 방법.
 *
 * 묻는 대신 하고 나서 되돌릴 수 있게 한다 — "정말 지울까요?" 는 사람에게 읽기를 시키고
 * 그 대가로 아무것도 막지 못한다. 업로드 현황과 같은 방식으로 화면 전체가 하나를 나눠
 * 쓴다. 목록에서 한 일도, 사이드바에 끌어다 놓은 일도 같은 자리에 뜬다.
 */
interface Undoable {
  message: string
  undo: () => Promise<unknown>
}

interface UndoState {
  current: Undoable | null
  offer: (action: Undoable) => void
  clear: () => void
}

const UndoContext = createContext<UndoState | null>(null)

export function UndoProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<Undoable | null>(null)
  const location = useLocation()

  // 되돌리기는 방금 그 자리에서만 뜻이 있다. 화면을 옮겨 다니면 치운다.
  useEffect(() => setCurrent(null), [location.pathname])

  const value = useMemo<UndoState>(
    () => ({
      current,
      offer: (action: Undoable) => setCurrent(action),
      clear: () => setCurrent(null),
    }),
    [current],
  )

  return <UndoContext.Provider value={value}>{children}</UndoContext.Provider>
}

export function useUndo(): UndoState {
  const context = useContext(UndoContext)
  if (!context) throw new Error('useUndo must be used inside UndoProvider')
  return context
}

export function UndoBanner() {
  const { current, clear } = useUndo()
  const [error, setError] = useState<string | null>(null)

  const run = useCallback(
    async (action: Undoable) => {
      clear()
      setError(null)
      try {
        await action.undo()
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err))
      }
    },
    [clear],
  )

  if (error !== null) {
    return (
      <p className="error banner" onClick={() => setError(null)}>
        되돌리지 못했습니다 — {error}
      </p>
    )
  }
  if (current === null) return null

  return (
    <p className="notice banner undoable">
      <span>{current.message}</span>
      <button type="button" className="link" onClick={() => void run(current)}>
        실행 취소
      </button>
    </p>
  )
}
